import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ArrowLeft, Plus, Trash2, Plane, Loader2, ChevronUp, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Today's exchange rate (mid-market, Sep 2026): ~120.8 ISK per USD — display only
const ISK_PER_USD = 120.8;
const fmtISK = (n) => `${Math.round(Number(n) || 0).toLocaleString("de-DE")} ISK`;
const fmtUSD = (n) => `$${Math.round((Number(n) || 0) / ISK_PER_USD).toLocaleString("en-US")}`;

const STAGES = [
  { key: "price_confirmed", label: "Price ✓" },
  { key: "booked", label: "Booked" },
  { key: "picked_up", label: "Picked up" },
  { key: "tested", label: "Tested" },
  { key: "loaded", label: "Loaded" },
  { key: "returned", label: "Returned" },
];

export default function AdminSideEvent() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [eventName, setEventName] = useState("Side Event");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState({ name: "", cost: "", alt_cost: "" });
  const [adding, setAdding] = useState(false);
  const [labels, setLabels] = useState({ cost_label: "Cost", alt_label: "Alternative price" });

  const load = () => api.get("/admin/side-event-items").then((r) => {
    setEventName(r.data.event_name);
    setItems((r.data.items || []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    if (r.data.labels) setLabels(r.data.labels);
  }).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const saveLabel = (field, value) => {
    setLabels((prev) => ({ ...prev, [field]: value }));
    api.put("/admin/side-event-labels", { [field]: value }).catch(() => toast.error("Could not save column name"));
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const patchItem = async (id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    try {
      await api.patch(`/admin/side-event-items/${id}`, patch);
    } catch (e) {
      toast.error("Could not save change");
      load();
    }
  };

  const moveItem = async (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[index], b = items[j];
    const next = items.map((it) => {
      if (it.id === a.id) return { ...it, order: b.order };
      if (it.id === b.id) return { ...it, order: a.order };
      return it;
    }).sort((x, y) => (x.order ?? 0) - (y.order ?? 0));
    setItems(next);
    try {
      await Promise.all([
        api.patch(`/admin/side-event-items/${a.id}`, { order: b.order }),
        api.patch(`/admin/side-event-items/${b.id}`, { order: a.order }),
      ]);
    } catch (e) { toast.error("Could not reorder"); load(); }
  };

  const addItem = async () => {
    if (!newItem.name.trim()) return toast.error("Enter an item name");
    setAdding(true);
    try {
      const r = await api.post("/admin/side-event-items", { name: newItem.name.trim(), cost: Number(newItem.cost) || 0 });
      if (Number(newItem.alt_cost) > 0) {
        await api.patch(`/admin/side-event-items/${r.data.item.id}`, { alt_cost: Number(newItem.alt_cost) });
        r.data.item.alt_cost = Number(newItem.alt_cost);
      }
      setItems((prev) => [...prev, r.data.item]);
      setNewItem({ name: "", cost: "", alt_cost: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add item");
    } finally {
      setAdding(false);
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Remove this item?")) return;
    setItems((prev) => prev.filter((it) => it.id !== id));
    try { await api.delete(`/admin/side-event-items/${id}`); }
    catch (e) { toast.error("Could not delete"); load(); }
  };

  const total = items.reduce((s, it) => s + (Number(it.cost) || 0), 0);
  const altTotal = items.reduce((s, it) => s + (Number(it.alt_cost) || 0), 0);
  const diff = altTotal - total;

  return (
    <div data-testid="admin-side-event" className="min-h-screen bg-background grain">
      <header className="border-b border-white/10 bg-[#050505]/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="font-display font-extrabold text-xl uppercase tracking-tighter">Immersion Festival</Link>
          <button data-testid="side-event-logout" onClick={doLogout} className="text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-white inline-flex items-center gap-2 transition-colors">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Plane className="text-sky-400" size={22} />
            <h1 data-testid="side-event-title" className="font-display font-extrabold uppercase tracking-tighter text-3xl md:text-4xl">{eventName}</h1>
          </div>
          <Link to="/admin" data-testid="back-to-dashboard" className="text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-white inline-flex items-center gap-2 transition-colors">
            <ArrowLeft size={15} /> Dashboard
          </Link>
        </div>
        <p className="text-gray-500 text-sm mb-10">Cost planner for the side event. Fill in each item cost to see if it adds up, reorder with the arrows, and tick off each stage: price confirmed, booked, picked up, tested, loaded into the vehicle and returned. USD shown at today's rate (~{ISK_PER_USD} ISK/$).</p>

        {loading ? (
          <div className="flex items-center gap-3 text-gray-500 py-20 justify-center"><Loader2 className="animate-spin" size={20} /> Loading…</div>
        ) : (
          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-sm" data-testid="side-event-table">
              <thead className="bg-[#0C0C0E]">
                <tr className="text-left text-xs uppercase tracking-[0.15em] text-gray-500">
                  <th className="py-4 px-3 text-center w-16">Order</th>
                  <th className="py-4 px-5 min-w-[220px]">Item</th>
                  <th className="py-4 px-5 text-right min-w-[190px]">
                    <input data-testid="cost-label-input" defaultValue={labels.cost_label} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== labels.cost_label) saveLabel("cost_label", e.target.value.trim()); }} className="w-full bg-transparent text-right uppercase tracking-[0.15em] text-gray-400 hover:text-white border-b border-transparent hover:border-white/20 focus:border-emerald-400/50 focus:text-white focus:outline-none" />
                  </th>
                  <th className="py-4 px-5 text-right min-w-[190px]">
                    <input data-testid="alt-label-input" defaultValue={labels.alt_label} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== labels.alt_label) saveLabel("alt_label", e.target.value.trim()); }} className="w-full bg-transparent text-right uppercase tracking-[0.15em] text-sky-400/80 hover:text-sky-300 border-b border-transparent hover:border-sky-400/20 focus:border-sky-400/50 focus:text-white focus:outline-none" />
                  </th>
                  {STAGES.map((s) => <th key={s.key} className="py-4 px-3 text-center">{s.label}</th>)}
                  <th className="py-4 px-3 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={5 + STAGES.length} className="py-10 px-5 text-center text-gray-600 uppercase tracking-[0.2em] text-xs">No items yet — add your first below</td></tr>
                )}
                {items.map((it, idx) => (
                  <tr key={it.id} className="border-t border-white/10" data-testid={`item-row-${it.id}`}>
                    <td className="py-3 px-3">
                      <div className="flex flex-col items-center gap-0.5">
                        <button data-testid={`item-up-${it.id}`} onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="text-gray-500 hover:text-white disabled:opacity-20 transition-colors"><ChevronUp size={16} /></button>
                        <button data-testid={`item-down-${it.id}`} onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} className="text-gray-500 hover:text-white disabled:opacity-20 transition-colors"><ChevronDown size={16} /></button>
                      </div>
                    </td>
                    <td className="py-3 px-5">
                      <input
                        data-testid={`item-name-${it.id}`}
                        defaultValue={it.name}
                        onBlur={(e) => { if (e.target.value !== it.name) patchItem(it.id, { name: e.target.value }); }}
                        className="w-full bg-transparent border-b border-transparent hover:border-white/20 focus:border-white/50 text-white focus:outline-none py-1"
                      />
                    </td>
                    <td className="py-3 px-5 text-right">
                      <input
                        data-testid={`item-cost-${it.id}`}
                        type="number" min="0"
                        defaultValue={it.cost || 0}
                        onBlur={(e) => { const v = Number(e.target.value) || 0; if (v !== it.cost) patchItem(it.id, { cost: v }); }}
                        className="w-full bg-black/40 border border-white/15 focus:border-white/50 text-white text-right focus:outline-none px-3 py-2"
                      />
                      <div data-testid={`item-cost-fmt-${it.id}`} className="text-[11px] text-gray-500 mt-1">{fmtISK(it.cost)} · ~{fmtUSD(it.cost)}</div>
                    </td>
                    <td className="py-3 px-5 text-right">
                      <input
                        data-testid={`item-alt-cost-${it.id}`}
                        type="number" min="0"
                        defaultValue={it.alt_cost || 0}
                        onBlur={(e) => { const v = Number(e.target.value) || 0; if (v !== (it.alt_cost || 0)) patchItem(it.id, { alt_cost: v }); }}
                        className="w-full bg-black/40 border border-sky-500/25 focus:border-sky-400/60 text-white text-right focus:outline-none px-3 py-2"
                      />
                      <div data-testid={`item-alt-cost-fmt-${it.id}`} className="text-[11px] text-gray-500 mt-1">{fmtISK(it.alt_cost || 0)} · ~{fmtUSD(it.alt_cost || 0)}</div>
                      {(Number(it.alt_cost) || 0) > 0 && (() => {
                        const d = (Number(it.alt_cost) || 0) - (Number(it.cost) || 0);
                        return <div data-testid={`item-diff-${it.id}`} className={`text-[11px] font-bold mt-0.5 ${d < 0 ? "text-emerald-400" : d > 0 ? "text-[#e07a6c]" : "text-gray-500"}`}>{d === 0 ? "same" : `${d < 0 ? "−" : "+"}${fmtISK(Math.abs(d))} ${d < 0 ? "saved" : "more"}`}</div>;
                      })()}
                    </td>
                    {STAGES.map((s) => (
                      <td key={s.key} className="py-3 px-3 text-center">
                        <input
                          type="checkbox"
                          data-testid={`item-${it.id}-${s.key}`}
                          checked={!!it[s.key]}
                          onChange={(e) => patchItem(it.id, { [s.key]: e.target.checked })}
                          className="w-4 h-4 accent-emerald-500 cursor-pointer"
                        />
                      </td>
                    ))}
                    <td className="py-3 px-3 text-right">
                      <button data-testid={`item-delete-${it.id}`} onClick={() => removeItem(it.id)} className="text-gray-500 hover:text-[#e07a6c] transition-colors"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {/* Add new item row */}
                <tr className="border-t border-white/10 bg-white/[0.02]">
                  <td className="py-3 px-3"></td>
                  <td className="py-3 px-5">
                    <input
                      data-testid="new-item-name"
                      value={newItem.name}
                      onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
                      placeholder="New item name…"
                      className="w-full bg-transparent border-b border-white/15 focus:border-white/50 text-white focus:outline-none py-1 placeholder:text-gray-600"
                    />
                  </td>
                  <td className="py-3 px-5 text-right">
                    <input
                      data-testid="new-item-cost"
                      type="number" min="0"
                      value={newItem.cost}
                      onChange={(e) => setNewItem({ ...newItem, cost: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
                      placeholder="0"
                      className="w-full bg-black/40 border border-white/15 focus:border-white/50 text-white text-right focus:outline-none px-3 py-2 placeholder:text-gray-600"
                    />
                  </td>
                  <td className="py-3 px-5 text-right">
                    <input
                      data-testid="new-item-alt-cost"
                      type="number" min="0"
                      value={newItem.alt_cost}
                      onChange={(e) => setNewItem({ ...newItem, alt_cost: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
                      placeholder="0"
                      className="w-full bg-black/40 border border-sky-500/25 focus:border-sky-400/60 text-white text-right focus:outline-none px-3 py-2 placeholder:text-gray-600"
                    />
                  </td>
                  <td colSpan={STAGES.length + 1} className="py-3 px-3">
                    <button data-testid="add-item-btn" onClick={addItem} disabled={adding} className="uppercase tracking-[0.12em] text-xs font-bold py-2.5 px-4 border border-white/15 text-white hover:border-white/50 transition-all inline-flex items-center gap-2 disabled:opacity-60">
                      <Plus size={14} /> {adding ? "Adding…" : "Add item"}
                    </button>
                  </td>
                </tr>
                {/* Total */}
                <tr className="border-t-2 border-white/20 bg-[#0C0C0E]">
                  <td className="py-5 px-3"></td>
                  <td className="py-5 px-5 uppercase tracking-[0.15em] text-xs text-gray-400 font-bold">Total</td>
                  <td data-testid="side-event-total" className="py-5 px-5 text-right">
                    <span className="block text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1 truncate">{labels.cost_label}</span>
                    <span className="font-display font-extrabold text-2xl text-emerald-400 block">{fmtISK(total)}</span>
                    <span data-testid="side-event-total-usd" className="text-xs text-gray-400">~{fmtUSD(total)}</span>
                  </td>
                  <td data-testid="side-event-alt-total" className="py-5 px-5 text-right">
                    <span className="block text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1 truncate">{labels.alt_label}</span>
                    <span className="font-display font-extrabold text-2xl text-sky-400 block">{fmtISK(altTotal)}</span>
                    <span data-testid="side-event-alt-total-usd" className="text-xs text-gray-400">~{fmtUSD(altTotal)}</span>
                  </td>
                  <td colSpan={STAGES.length + 1} className="py-5 px-4 text-right">
                    <span className="block text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1">Alt vs current</span>
                    <span data-testid="side-event-diff" className={`font-display font-bold text-lg ${diff < 0 ? "text-emerald-400" : diff > 0 ? "text-[#e07a6c]" : "text-gray-400"}`}>{diff === 0 ? "No difference" : `${diff < 0 ? "−" : "+"}${fmtISK(Math.abs(diff))}`}</span>
                    {diff !== 0 && <span className="block text-[11px] text-gray-500">~{diff < 0 ? "−" : "+"}{fmtUSD(Math.abs(diff))} · {diff < 0 ? "cheaper" : "more expensive"}</span>}
                    <span className="block text-[11px] text-gray-600 mt-1">{items.length} item{items.length === 1 ? "" : "s"}</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
